import classNames from 'classnames';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { DemoPill, FilterSelect, SearchInput, Segmented, StateMessage } from '@/components/ui/controls';
import { Badge, PageHeader } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import { btn, table } from '@/components/ui/styles';
import { formatDate } from '@/utils/format';
import { useApi } from '@/utils/use-api';
import { dateValue, useSort, type SortColumns } from '@/utils/use-sort';

import { CATEGORY_LABELS, type ArticleCategory, type KBArticle } from './api-types';
import KnowledgeNav, { KnowledgeNote } from './knowledge-nav';

const COLUMNS: SortColumns<KBArticle> = {
  title: { value: (a) => a.title },
  updated: { value: (a) => dateValue(a.updated_at), desc: true },
  used: { value: (a) => a.times_used, desc: true },
};

const CATEGORIES = Object.keys(CATEGORY_LABELS) as ArticleCategory[];

/**
 * Knowledge base articles (REM-2, REM-3): what the bot answers
 * clients from (GET /knowledge/articles). It drafts an article's short answer, and a
 * person confirms it before it is sent. On a phone the table keeps one column, with the category,
 * date and use folded under the title (UI-1).
 */
const ArticlesPage = () => {
  const navigate = useNavigate();
  const { data, error } = useApi<KBArticle[]>('/knowledge/articles');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<'all' | ArticleCategory>('all');

  const articles = data ?? [];
  const q = query.trim().toLowerCase();
  const { rows, sort, toggle } = useSort(
    articles.filter((a) => (category === 'all' || a.category === category) && (!q || `${a.title} ${a.short_answer}`.toLowerCase().includes(q))),
    COLUMNS,
  );
  const open = (id: string) => navigate(`/knowledge/${encodeURIComponent(id)}`);

  return (
    <div>
      <PageHeader
        title="Knowledge base"
        description="What the bot knows. It drafts short answers from these articles, and a person confirms each one before it is sent."
        actions={
          <Link to="/knowledge/new" className={btn.primary}>
            New article
          </Link>
        }
      >
        <KnowledgeNav />
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchInput value={query} onChange={setQuery} label="Search articles" placeholder="Search titles and answers" />
          <div className="md:hidden">
            <FilterSelect label="Category" idle="all" value={category} onChange={(v) => setCategory(v as 'all' | ArticleCategory)}>
              <option value="all">All categories</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </FilterSelect>
          </div>
          <div className="hidden md:block">
            <Segmented
              label="Category"
              value={category}
              onChange={setCategory}
              options={[{ value: 'all' as const, label: 'All' }, ...CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))]}
            />
          </div>
        </div>
      </PageHeader>
      <KnowledgeNote />

      {error && !data ? (
        <StateMessage title="Articles could not be loaded" tone="error">
          {error}
        </StateMessage>
      ) : !data ? (
        <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
      ) : rows.length === 0 ? (
        <StateMessage title={articles.length ? 'No article matches' : 'No articles yet'}>
          {articles.length ? 'Try another search or category.' : 'Write the first one: the question clients ask, and the short answer the bot gives.'}
        </StateMessage>
      ) : (
        <div className={table.wrap}>
          <div className="overflow-x-auto">
            <table className={classNames(table.table, 'md:min-w-[760px]')}>
              <thead className={table.head}>
                <tr>
                  <SortTh label="Article" column="title" sort={sort} onSort={toggle} />
                  <th scope="col" className={classNames(table.th, 'hidden md:table-cell')}>Category</th>
                  <SortTh label="Updated" column="updated" sort={sort} onSort={toggle} className="hidden md:table-cell" />
                  <SortTh label="Used" column="used" sort={sort} onSort={toggle} right className="hidden md:table-cell" />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr
                    key={a.id}
                    tabIndex={0}
                    onClick={() => open(a.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        open(a.id);
                      }
                    }}
                    className={classNames(table.rowLink, 'align-top focus:outline-none focus-visible:bg-surface-hover')}
                  >
                    <td className={classNames(table.td, 'max-w-xl')}>
                      <p className="flex flex-wrap items-center gap-2 text-fg-strong">
                        {a.title}
                        {a.demo && <DemoPill title="Made up by the local demo seed">Demo</DemoPill>}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-xs text-fg-muted">{a.short_answer}</p>
                      <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted md:hidden">
                        <Badge>{CATEGORY_LABELS[a.category] ?? a.category}</Badge>
                        <span>
                          {formatDate(a.updated_at)} · used {a.times_used}
                        </span>
                      </p>
                    </td>
                    <td className={classNames(table.td, 'hidden md:table-cell')}>
                      <Badge>{CATEGORY_LABELS[a.category] ?? a.category}</Badge>
                    </td>
                    <td className={classNames(table.td, 'hidden whitespace-nowrap tabular-nums text-fg-muted md:table-cell')}>{formatDate(a.updated_at)}</td>
                    <td className={classNames(table.td, 'hidden text-right tabular-nums text-fg md:table-cell')} title="Bot answers that used this article">
                      {a.times_used}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t border-line px-4 py-3 text-sm text-fg-muted">
            {rows.length} of {articles.length} articles
          </p>
        </div>
      )}
    </div>
  );
};

export default ArticlesPage;
